/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React from 'react';
import { Ducks, Time, TimeUtils } from '@wit/vtv-x-foundation-lib';
import translate from 'services/i18n/translate';
import { getDayName } from 'services/utils/DateTimeUtils';
import { DropdownCalendarProps } from 'types/components/Dropdown';
import { useSelector } from 'react-redux';
import { Close } from '..';

function DropdownCalendar({ closeDropdown, selectedDay, setSelectedDay }: DropdownCalendarProps): JSX.Element {
  const now = TimeUtils.now();
  const epgBackwardDuration = useSelector(Ducks.Epg.Selectors.getEpgBackwardDuration);
  const epgForwardDuration = useSelector(Ducks.Epg.Selectors.getEpgForwardDuration);

  const renderDates = (): JSX.Element => {
    let newCalendar: Time[] = [];
    const negativeTimeSpan = TimeUtils.subtractDuration(now, epgBackwardDuration);
    const positiveTimeSpan = TimeUtils.addDuration(now, epgForwardDuration);
    const weekDay = new Date(now).getDay() || 7; // get day of the week and convert Sunday to 7
    const emptySpaces = weekDay - 1;
    newCalendar = Array(emptySpaces).fill(null);

    for (
      let tempDay = negativeTimeSpan;
      TimeUtils.isBeforeOrEqual(tempDay, positiveTimeSpan);
      tempDay = TimeUtils.addDays(tempDay, 1)
    ) {
      newCalendar.push(tempDay);
    }

    return (
      <ul className="card-body-dates">
        {newCalendar.map((e, index) => {
          const key = index;
          if (!e) {
            return <li className="empty" key={`${key}-empty`} />;
          }
          return (
            <li
              className={`${TimeUtils.isSameDay(e, selectedDay) ? 'active' : ''} ${
                TimeUtils.toDestructuredTime(e).month === TimeUtils.toDestructuredTime(now).month ? '' : 'prev'
              }`}
              key={`${key}-${e}`}
              onClick={() => {
                if (!TimeUtils.isSameDay(e, selectedDay)) {
                  setSelectedDay(e);
                }
              }}
              id={`DropdownCalendar_day___${TimeUtils.toDestructuredTime(e).day}`}
            >
              {TimeUtils.toDestructuredTime(e).day}
              {TimeUtils.isSameDay(e, selectedDay) && <span />}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="dropdown-calendar" id="DropdownCalendar">
      <div className="card">
        <div className="card-header">
          <div className="date-wrapper">
            <h4 id="DropdownCalendar_dayName">{getDayName(selectedDay)}</h4>
            <h4 id="DropdownCalendar_dayAndMonth" className="month">
              - {TimeUtils.toDayShortMonthFormat(selectedDay)}
            </h4>
          </div>
          <Close bemBlock="DropdownCalendar" action={closeDropdown} isActive />
        </div>
        <div className="card-body">
          <ul className="card-body-days" id="DropdownCalendar_days">
            <li>{translate['date.monday']}</li>
            <li>{translate['date.tuesday']}</li>
            <li>{translate['date.wednesday']}</li>
            <li>{translate['date.thursday']}</li>
            <li>{translate['date.friday']}</li>
            <li>{translate['date.saturday']}</li>
            <li>{translate['date.sunday']}</li>
          </ul>
          {renderDates()}
        </div>
      </div>
    </div>
  );
}

export default React.memo(DropdownCalendar);
